import * as CdsIcons from '@central-design-system/icons';
import { computed } from 'vue';

import { EventProp, paramCase, propsFactory } from '@components/utils';

import type { TCdsIconsName } from '@central-design-system/icons';
import type { PropType, ComputedRef } from 'vue';

export interface IIconProps {
  prependIcon: TCdsIconsName | undefined;
  appendIcon: TCdsIconsName | undefined;
  'onClick:prepend'?: EventProp | undefined;
  'onClick:append'?: EventProp | undefined;
}
export interface IUseIcon {
  hasPrepend: ComputedRef<boolean>;
  hasAppend: ComputedRef<boolean>;
  iconClasses: ComputedRef<Record<string, boolean>>;
  handleClickPrepend: (event: MouseEvent) => void;
  handleClickAppend: (event: MouseEvent) => void;
}

export const makeIconProps = propsFactory(
  {
    /**
     * Иконка в начале элемента
     */
    prependIcon: {
      type: String as PropType<TCdsIconsName>,
      default: undefined,
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    },
    /**
     * Иконка в конце элемента
     */
    appendIcon: {
      type: String as PropType<TCdsIconsName>,
      default: undefined,
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    }
  },
  'icon'
);

export const makeIconEvents = propsFactory(
  {
    /**
     * Событие клика по иконке перед значением поля
     */
    'onClick:prepend': {
      type: Function as PropType<EventProp>,
      default: undefined
    },
    /**
     * Событие клика по иконке после значения поля
     */
    'onClick:append': {
      type: Function as PropType<EventProp>,
      default: undefined
    }
  },
  'icon-events'
);

export function useIcon(props: IIconProps, slots: Record<string, unknown>, name: string): IUseIcon {
  const hasPrepend = computed((): boolean => !!slots.prepend || !!props.prependIcon);
  const hasAppend = computed((): boolean => !!slots.append || !!props.appendIcon);

  const iconClasses = computed(() => ({
    [`${paramCase(name)}--has-prepend`]: hasPrepend.value,
    [`${paramCase(name)}--has-append`]: hasAppend.value,
    [`${paramCase(name)}--has-prepend-clickable`]: !!props['onClick:prepend'],
    [`${paramCase(name)}--has-append-clickable`]: !!props['onClick:append']
  }));

  const handleClickPrepend = props['onClick:prepend'] as (event: MouseEvent) => void;
  const handleClickAppend = props['onClick:append'] as (event: MouseEvent) => void;

  return {
    hasPrepend,
    hasAppend,
    iconClasses,
    handleClickPrepend,
    handleClickAppend
  };
}
